import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * SEOHead: lightweight meta tags + JSON-LD helper using react-helmet
 * Usage:
 * <SEOHead title="..." description="..." url="/community/123" keywords={["fbr","invoice"]} noindex={false} />
 */
export default function SEOHead({ 
  title, 
  description, 
  url = '', 
  keywords = [], 
  image, 
  schemaType = 'DiscussionForumPosting',
  noindex = false
}) {
  const fullUrl = typeof window !== 'undefined' 
    ? (url ? `https://fbrstapp.com${url.startsWith('/') ? url : `/${url}`}` : window.location.href)
    : `https://fbrstapp.com${url || ''}`;

  const safeTitle = title ? (title.includes('FBRSTAPP') ? title : `${title} | FBRSTAPP`) : 'FBRSTAPP - FBR Digital Invoicing Pakistan';
  const safeDesc = description?.slice(0, 160) || 'Ask questions and share tips about FBR Digital Invoicing in Pakistan.';
  const kw = Array.isArray(keywords) ? keywords.filter(Boolean).join(', ') : String(keywords || '');

  const baseSchema = {
    '@context': 'https://schema.org',
    headline: safeTitle,
    description: safeDesc,
    text: safeDesc,
    url: fullUrl,
    datePublished: new Date().toISOString(),
    author: {
      '@type': 'Person',
      name: 'FBRSTAPP Community'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl
    },
    publisher: {
      '@type': 'Organization',
      name: 'FBRSTAPP',
      url: 'https://fbrstapp.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://fbrstapp.com/fbrlogo1.svg'
      }
    },
    ...(image && { image: image }),
  };

  const jsonLd = {
    ...baseSchema,
    '@type': schemaType
  };

  return (
    <Helmet>
      {/* Basic SEO */}
      <title>{safeTitle}</title>
      <meta name="description" content={safeDesc} />
      {kw && <meta name="keywords" content={kw} />}
      {noindex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={safeDesc} />
      <meta property="og:url" content={fullUrl} />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:site_name" content="FBRSTAPP" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={safeDesc} />
      {image && <meta name="twitter:image" content={image} />}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
    </Helmet>
  );
}